import type { AccessibleResourceName, CompanySettings } from '@/types'

type userPermissionType = {
  accessible_resource: {
    name: AccessibleResourceName
    resource_type: string
  }
  action: string
}

// Permissions from Backoffice:
const MANAGE = 'manage' // [view, create, update, delete]
const WRITE = 'write' // [view, create, update]
const READ = 'read' // [view]

let userPermissions: userPermissionType[]
let companySettings: CompanySettings

const findPermissionAction = (resourceName: string) => {
  const permission = userPermissions.find((permission: userPermissionType) => {
    return permission.accessible_resource.name === resourceName
  })
  return permission ? permission.action : ''
}

/**
 * Checks if a field is editable based on company's manager overrides (editable_user_fields).
 * Fields in the array are unlocked for editing on synced users.
 */
const isFieldEditable = (field?: string) => {
  if (!field) return true

  const isUserFieldEditable = companySettings?.editable_user_fields.some(
    (mapping) => mapping === field
  )
  return isUserFieldEditable
}

const can = (resourceName: AccessibleResourceName, actions: string[]) => {
  const action = findPermissionAction(resourceName)
  return actions.includes(action)
}

/**
 * Checks if the user has permission to edit a resource.
 * In some cases fields may be blocked even if the user has permission to edit the resource, due to external data sync.
 * @param resourceName
 * @param actions
 * @param field - if checking for field-level permissions, pass the field name
 * @returns
 */
const canEdit = (
  resourceName: AccessibleResourceName,
  actions: string[],
  field?: string
) => {
  const hasPermission = can(resourceName, actions)

  const isEditable = isFieldEditable(field)

  return hasPermission && isEditable
}

const userPermission = {
  setUserPermissionsData(permissions: userPermissionType[]) {
    userPermissions = permissions
  },
  setCompanySettingsData(settings: CompanySettings) {
    companySettings = settings
  },

  canRead(resourceName: AccessibleResourceName) {
    return can(resourceName, [READ, WRITE, MANAGE])
  },

  canWrite(resourceName: AccessibleResourceName, field?: string) {
    return canEdit(resourceName, [WRITE, MANAGE], field)
  },

  canManage(resourceName: AccessibleResourceName, field?: string) {
    return canEdit(resourceName, [MANAGE], field)
  },
}

export const UserPermissionPlugin = (Vue: any) => {
  Vue.prototype.$userPermission = userPermission
}

export type UserPermission = typeof userPermission
